import { useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { useCreateProductMutation, CreateProductInput } from '@/graphql/main';

type Props = {
    successCallback?: () => void;
};

const CreateProductModal = ({ successCallback }: Props) => {
    const [createProductMutation] = useCreateProductMutation();
    const [form] = Form.useForm<CreateProductInput>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { t } = useTranslation('translation');

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleCreateProduct = async () => {
        try {
            const values = await form.validateFields();
            await createProductMutation({
                variables: {
                    input: {
                        name: values.name,
                        price: Number(parseFloat(values.price).toFixed(2)),
                        isActive: values.isActive
                    }
                },
                onCompleted: async () => {
                    successCallback?.();
                    await message.success(t('CREATE_PRODUCT_SUCCESS'));
                },
                onError: async () => await message.error(t('CREATE_PRODUCT_FAIL'))
            });
            form.resetFields();
            setIsModalOpen(false);
        } catch {
            await message.error(t('CREATE_PRODUCT_FAIL'));
        } finally {
            form.resetFields();
        }
    };

    return (
        <>
            <Row justify="end" style={{ marginTop: 18 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
                    {t('NEW_PRODUCT')}
                </Button>
            </Row>
            <Modal
                title={t('CREATE_NEW_PRODUCT')}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        {t('CANCEL')}
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleCreateProduct}>
                        {t('CREATE_PRODUCT')}
                    </Button>
                ]}>
                <Form form={form}>
                    <Form.Item
                        name="name"
                        label={t('NAME')}
                        labelCol={{ span: 24 }}
                        rules={[{ required: true, message: t('NAME_REQUIRED') }]}>
                        <Input placeholder={t('NAME')} />
                    </Form.Item>

                    <Form.Item
                        name="price"
                        label={t('PRICE')}
                        labelCol={{ span: 24 }}
                        rules={[{ required: true, message: t('PRICE_REQUIRED') }]}>
                        <Input placeholder={t('PRICE')} type="number" min="0.01" step="0.01" />
                    </Form.Item>

                    <Form.Item
                        label={t('ACTIVE')}
                        name="isActive"
                        labelCol={{ span: 24 }}
                        rules={[{ required: true, message: t('ACTIVE_STATUS_REQUIRED') }]}>
                        <Select placeholder={t('ACTIVE')}>
                            <Select.Option value={true}>{t('YES')}</Select.Option>
                            <Select.Option value={false}>{t('NO')}</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default CreateProductModal;
